/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useRef, useState} from 'react';
import {debounce, isEmpty} from 'lodash';

import {FIRST_WEEK, FIRST_WEEK_COMPLETE} from '/enum/clientJourneyTasks.enum';

import {
  getNewTasks,
  getTaskDataToSave,
  getTaskDataWithMeta,
  getTasksOnChange,
  getTasksWithLabel,
  getUpdatedTasks,
} from '../../ClientJourneyTask.util';

/**
 * @param {object} params - Parameters
 * @param {Object[]} params.firstWeekInitialTasks - an object that contains title data (description)
 * @param {string}   params.firstWeekInitialTasks.tabDescription
 * @param {string}   params.firstWeekInitialTasks.screenTabCode
 * @param {Object[]} params.firstWeekInitialTasks.checkBox
 * @param {string}   params.firstWeekInitialTasks.checkBox.screenTabCode
 * @param {string}   params.firstWeekInitialTasks.checkBox.chkBoxCode
 * @param {string}   params.firstWeekInitialTasks.checkBox.description
 * @param {Object[]} params.firstWeekTasks - an object that contains data about the status of the checkbox
 * @param {number}   params.firstWeekTasks.clientId
 * @param {string}   params.firstWeekTasks.screenTabCode
 * @param {Object[]} params.firstWeekTasks.checkIns
 * @param {string}   params.firstWeekTasks.checkIns.chkBoxCode
 * @param {boolean}  params.firstWeekTasks.checkIns.checkStatus
 * @param {function} params.fetchClientJourneyTask - function for sending a post request to change the status of the checkbox
 * @param {function} params.setClientJourneyTasks - function for changing the status in the redux
 *
 * @returns {object} tasks - object for displaying data in a FlatList
 * @returns {function} onTaskStatusChange - function fur changing task status
 * @returns {function} onFirstWeekCompleteChange - function fur changing entire first week task status
 *  @param {number}   clientJourneyTimer - remote config timer variable to send back the request with new task staus
 */

export const useFirstWeekTasks = ({
  firstWeekInitialTasks,
  firstWeekTasks,
  clientId,
  clientJourneyTimer,
  fetchClientJourneyTask,
  setClientJourneyTasks,
  setClientJourneyState,
}: any) => {
  const firstWeekCompleteItem = {
    id: 1,
    label: FIRST_WEEK_COMPLETE,
    checkStatus: false,
  };
  const tasksRef = useRef([]);
  const timer = useRef(null);

  const [newFirstWeekTasks, setNewFirstWeekTasks] = useState([]);
  const [firstWeekCompleteData, setFirstWeekCompleteData] = useState({});
  const [tasks, setTasks] = useState([]);
  const [isCheckboxStatusChanged, setIsCheckboxStatusChanged] = useState(false);

  const saveFirstWeekTaskData = (data: any) => {
    // Send the request with new data
    fetchClientJourneyTask({
      task: getTaskDataWithMeta(newFirstWeekTasks, data, FIRST_WEEK),
    });
  };

  const onTaskStatusChange = (status: any, id: any) => {
    setIsCheckboxStatusChanged(true);

    const tasksAfterStatusChange = getTasksOnChange(tasks, id, status);

    setFirstWeekCompleteData({
      ...firstWeekCompleteData,
      checkStatus: tasksAfterStatusChange.every(
        (task: any) => task.checkStatus === true,
      ),
    });
    setTasks(tasksAfterStatusChange);

    // Save it in redux to use the updated data and avoid delays due to the request
    setClientJourneyTasks({
      // @ts-expect-error TS(2554): Expected 4 arguments, but got 3.
      tasks: getUpdatedTasks(
        tasksAfterStatusChange,
        newFirstWeekTasks,
        FIRST_WEEK,
      ),
    });

    // @ts-expect-error TS(2769): No overload matches this call.
    clearTimeout(timer.current);
  };

  const onFirstWeekCompleteChange = (status: any, _id: any) => {
    setIsCheckboxStatusChanged(true);
    setFirstWeekCompleteData({
      ...firstWeekCompleteData,
      // @ts-expect-error TS(2339): Property 'checkStatus' does not exist on type '{}'... Remove this comment to see the full error message
      checkStatus: !firstWeekCompleteData.checkStatus,
    });
    const tasksAfterStatusChange = tasks.map(task => ({
      // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
      ...task,
      checkStatus: status === true,
    }));
    // @ts-expect-error TS(2345): Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
    setTasks(tasksAfterStatusChange);

    // Save it in redux to use the updated data and avoid delays due to the request
    setClientJourneyTasks({
      // @ts-expect-error TS(2554): Expected 4 arguments, but got 2.
      tasks: getUpdatedTasks(tasksAfterStatusChange, newFirstWeekTasks),
    });
    // @ts-expect-error TS(2769): No overload matches this call.
    clearTimeout(timer.current);
  };

  useEffect(() => {
    setIsCheckboxStatusChanged(false);
    if (isCheckboxStatusChanged) {
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'null'.
      timer.current = setTimeout(() => {
        debounce(
          saveFirstWeekTaskData,
          clientJourneyTimer,
        )(getTaskDataToSave(tasksRef?.current));
      }, clientJourneyTimer);

      // calling the client journey loading state to reload ClientsToContact nextTask filter
      setClientJourneyState();
    }
  }, [isCheckboxStatusChanged]);

  useEffect(() => {
    if (!isEmpty(newFirstWeekTasks) && !isEmpty(firstWeekInitialTasks)) {
      const updatedTasks = getTasksWithLabel(
        newFirstWeekTasks,
        firstWeekInitialTasks,
      );
      // @ts-expect-error TS(2345): Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
      setTasks([...updatedTasks]);
      setFirstWeekCompleteData({
        ...firstWeekCompleteItem,
        checkStatus: updatedTasks.every(
          (task: any) => task.checkStatus === true,
        ),
      });
    }
  }, [newFirstWeekTasks, firstWeekInitialTasks]);

  useEffect(() => {
    if (!isEmpty(firstWeekTasks)) {
      setNewFirstWeekTasks(firstWeekTasks);
    }
    if (!isEmpty(firstWeekInitialTasks) && isEmpty(firstWeekTasks)) {
      // @ts-expect-error TS(2345): Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
      setNewFirstWeekTasks(getNewTasks(clientId, firstWeekInitialTasks));
    }
  }, [firstWeekTasks, firstWeekInitialTasks]);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  return {
    firstWeekCompleteData,
    tasks,
    onTaskStatusChange,
    onFirstWeekCompleteChange,
  };
};
